/**
 * Procedural Clearings for LUMEN
 * 
 * Designed to instantiate the four components of Attention Restoration Theory:
 * - Soft Fascination (effortless capture)
 * - Being Away (different from usual task space)
 * - Extent (coherent, immersive world)
 * - Compatibility (you want to be there)
 *
 * All rendered with Canvas 2D + pure math (no external assets).
 * Supports mouse "attention attractors" and DeviceOrientation tilt.
 */

import type { SceneFamily } from './types';

export interface SceneParams {
  intensity: number;     // 0.3–1.4 user slider
  time: number;
  mouseX: number;        // 0..1
  mouseY: number;
  tiltX: number;         // -1..1 (left/right)
  tiltY: number;         // -1..1 (forward/back)
}

export type SceneRenderer = (ctx: CanvasRenderingContext2D, w: number, h: number, p: SceneParams) => void;

const rand = (min: number, max: number) => min + Math.random() * (max - min);

export class ClearingScene {
  family: SceneFamily;
  renderer: SceneRenderer;
  private raf = 0;
  private start = performance.now();
  private params: SceneParams = { intensity: 0.85, time: 0, mouseX: 0.5, mouseY: 0.5, tiltX: 0, tiltY: 0 };
  private mouseInside = false;

  // Per-scene state for organic motion
  private leaves: Array<{x: number; y: number; phase: number; size: number; sway: number}> = [];
  private ripples: Array<{x: number; y: number; r: number; life: number; amp: number}> = [];
  private grass: Array<{x: number; baseH: number; phase: number; width: number}> = [];

  constructor(family: SceneFamily, canvas: HTMLCanvasElement) {
    this.family = family;
    const ctx = canvas.getContext('2d', { alpha: true })!;

    // Initialize scene-specific particles / blades
    if (family === 'canopy') this.initCanopy(canvas);
    if (family === 'ripples') this.initRipples(canvas);
    if (family === 'meadow') this.initMeadow(canvas);

    const loop = () => {
      const now = performance.now();
      this.params.time = (now - this.start) / 1000;

      ctx.fillStyle = '#0a0f0b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      this.renderer(ctx, canvas.width, canvas.height, this.params);

      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);

    // Mouse / pointer interaction (attention attractor)
    const rect = () => canvas.getBoundingClientRect();
    const updateMouse = (clientX: number, clientY: number) => {
      const r = rect();
      this.params.mouseX = (clientX - r.left) / r.width;
      this.params.mouseY = (clientY - r.top) / r.height;
    };

    canvas.addEventListener('pointermove', (e) => {
      this.mouseInside = true;
      updateMouse(e.clientX, e.clientY);
    });
    canvas.addEventListener('pointerleave', () => {
      this.mouseInside = false;
      // gently return to center
      this.params.mouseX = 0.5;
      this.params.mouseY = 0.45;
    });

    // Touch support (mobile Chrome)
    canvas.addEventListener('touchmove', (e) => {
      if (e.touches[0]) updateMouse(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });

    // Device orientation (Chrome on phones + some laptops)
    const handleOrientation = (e: DeviceOrientationEvent) => {
      // beta: front/back, gamma: left/right
      if (e.gamma != null) this.params.tiltX = Math.max(-1, Math.min(1, e.gamma / 45));
      if (e.beta != null) this.params.tiltY = Math.max(-1, Math.min(1, (e.beta - 45) / 45));
    };
    if (typeof DeviceOrientationEvent !== 'undefined') {
      // Chrome requires permission on iOS; on Android it often just works on https
      window.addEventListener('deviceorientation', handleOrientation);
    }

    // Assign renderer
    if (family === 'canopy') this.renderer = this.renderCanopy.bind(this);
    else if (family === 'ripples') this.renderer = this.renderRipples.bind(this);
    else this.renderer = this.renderMeadow.bind(this);
  }

  setIntensity(v: number) { this.params.intensity = v; }
  setMouse(x: number, y: number) { this.params.mouseX = x; this.params.mouseY = y; }

  destroy() {
    cancelAnimationFrame(this.raf);
  }

  // ---------- CANOPY ----------
  private initCanopy(c: HTMLCanvasElement) {
    const n = 38;
    for (let i = 0; i < n; i++) {
      this.leaves.push({
        x: (i / n) * c.width + (Math.random() - 0.5) * 60,
        y: 60 + Math.random() * (c.height * 0.65),
        phase: Math.random() * Math.PI * 2,
        size: 9 + Math.random() * 13,
        sway: 0.6 + Math.random() * 0.9,
      });
    }
  }

  private renderCanopy(ctx: CanvasRenderingContext2D, w: number, h: number, p: SceneParams) {
    const t = p.time * (0.6 + p.intensity * 0.4);
    const tilt = p.tiltX * 14;

    // Sky / canopy ceiling
    const grad = ctx.createLinearGradient(0, 0, 0, h * 0.55);
    grad.addColorStop(0, '#1f2a22');
    grad.addColorStop(1, '#162019');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h * 0.6);

    // Light shafts (soft fascination)
    ctx.save();
    ctx.globalAlpha = 0.06 + p.intensity * 0.035;
    for (let i = -1; i <= 1; i++) {
      const x = w * (0.35 + i * 0.22) + tilt * 0.6;
      ctx.fillStyle = '#f4e9d8';
      ctx.beginPath();
      ctx.moveTo(x - 90, 0);
      ctx.lineTo(x + 110, 0);
      ctx.lineTo(x + 28 + Math.sin(t * 0.3) * 9, h * 0.72);
      ctx.lineTo(x - 34, h * 0.72);
      ctx.fill();
    }
    ctx.restore();

    // Leaves — layered, coherent slow sway + local mouse disturbance
    ctx.fillStyle = '#3a4a36';
    ctx.strokeStyle = '#4e5f47';
    ctx.lineWidth = 1.2;

    for (const leaf of this.leaves) {
      const mx = (p.mouseX - 0.5) * 2;
      const dist = Math.hypot((leaf.x / w - p.mouseX), (leaf.y / h - p.mouseY) * 0.6);
      const attract = Math.max(0, 1 - dist * 2.8) * 11 * (this.mouseInside ? 1 : 0.3);

      const sway = Math.sin(t * 1.15 * leaf.sway + leaf.phase) * (5.5 + p.intensity * 2.5) + attract * 0.6 + tilt * 0.3;

      ctx.save();
      ctx.translate(leaf.x + sway, leaf.y);
      ctx.rotate(Math.sin(t * 0.7 + leaf.phase) * 0.18 + mx * 0.06);

      // Leaf shape (simple elegant)
      ctx.beginPath();
      ctx.ellipse(0, 0, leaf.size * 1.15, leaf.size * 0.48, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      // Vein
      ctx.strokeStyle = 'rgba(70, 82, 63, 0.55)';
      ctx.beginPath();
      ctx.moveTo(-leaf.size * 0.7, 0);
      ctx.lineTo(leaf.size * 0.75, 0);
      ctx.stroke();
      ctx.restore();
    }

    // Ground / forest floor
    ctx.fillStyle = '#141c17';
    ctx.fillRect(0, h * 0.68, w, h * 0.4);
  }

  // ---------- RIPPLES ----------
  private initRipples(c: HTMLCanvasElement) {
    for (let i = 0; i < 5; i++) {
      this.ripples.push({
        x: c.width * (0.2 + i * 0.14),
        y: c.height * (0.42 + Math.random() * 0.22),
        r: 18 + i * 7,
        life: 12 + i * 1.5,
        amp: 0.6 + Math.random() * 0.7,
      });
    }
  }

  private renderRipples(ctx: CanvasRenderingContext2D, w: number, h: number, p: SceneParams) {
    const t = p.time * (0.55 + p.intensity * 0.25);

    // Deep water
    ctx.fillStyle = '#0f1815';
    ctx.fillRect(0, 0, w, h);

    // Horizon / sky reflection band
    const sky = ctx.createLinearGradient(0, h * 0.18, 0, h * 0.48);
    sky.addColorStop(0, '#2a3530');
    sky.addColorStop(1, '#162019');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, w, h * 0.48);

    // Primary overlapping sine ripples (extent + soft fascination)
    ctx.strokeStyle = 'rgba(163, 176, 158, 0.55)';
    ctx.lineWidth = 1.35;

    const cx = w * (0.5 + p.tiltX * 0.06);
    const cy = h * (0.46 + p.tiltY * 0.03);

    for (let k = 0; k < 4; k++) {
      const baseR = 38 + k * 29 + Math.sin(t * 0.4 + k) * 5;
      ctx.beginPath();
      for (let a = 0; a <= Math.PI * 2 + 0.05; a += 0.065) {
        const r = baseR + Math.sin(a * (2.2 + k * 0.3) + t * (1.1 + k * 0.2)) * (6.5 + p.intensity * 3.5);
        const x = cx + Math.cos(a) * r;
        const y = cy + Math.sin(a * 0.9) * r * 0.42;
        if (a === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // Occasional stronger "drop" ripples from mouse (user can "touch" the water)
    if (this.mouseInside) {
      const mx = w * p.mouseX;
      const my = h * p.mouseY * 0.82;
      ctx.strokeStyle = 'rgba(232, 217, 184, 0.35)';
      ctx.lineWidth = 1.6;
      for (let i = 0; i < 3; i++) {
        const r = (t * 38 + i * 29) % 110;
        ctx.beginPath();
        ctx.ellipse(mx, my, r * 1.1, r * 0.38, 0.2, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // Subtle surface texture
    ctx.fillStyle = 'rgba(255,255,255,0.015)';
    for (let i = 0; i < 7; i++) {
      const x = ((i * 137) % w);
      ctx.fillRect(x, h * 0.31 + Math.sin(t + i) * 9, 1.5, h * 0.19);
    }
  }

  // ---------- MEADOW ----------
  private initMeadow(c: HTMLCanvasElement) {
    const n = 62;
    for (let i = 0; i < n; i++) {
      this.grass.push({
        x: (i / n) * c.width + (Math.random() - 0.5) * 18,
        baseH: 38 + Math.random() * 52,
        phase: Math.random() * 6.28,
        width: 1.4 + Math.random() * 0.9,
      });
    }
  }

  private renderMeadow(ctx: CanvasRenderingContext2D, w: number, h: number, p: SceneParams) {
    const t = p.time * (0.48 + p.intensity * 0.32);
    const wind = p.tiltX * 0.6 + (p.mouseX - 0.5) * 1.1;

    // Sky + distant soft hills (extent)
    ctx.fillStyle = '#1c2621';
    ctx.fillRect(0, 0, w, h * 0.55);

    ctx.fillStyle = '#26342c';
    ctx.beginPath();
    ctx.moveTo(0, h * 0.38);
    ctx.quadraticCurveTo(w * 0.3, h * 0.21, w * 0.55, h * 0.39);
    ctx.quadraticCurveTo(w * 0.78, h * 0.27, w, h * 0.36);
    ctx.lineTo(w, h * 0.55);
    ctx.lineTo(0, h * 0.55);
    ctx.fill();

    // Grass blades — large scale coherent motion + local disturbance
    ctx.strokeStyle = '#3f513b';
    ctx.lineCap = 'round';

    for (const g of this.grass) {
      const dist = Math.hypot((g.x / w - p.mouseX) * 0.9, 0.3);
      const local = Math.max(0, 1.1 - dist * 2.4) * (this.mouseInside ? 1 : 0.4) * 8;

      const sway = Math.sin(t * 1.7 + g.phase) * (7 + p.intensity * 5) + wind * 4.5 + local;

      const x = g.x;
      const y0 = h * 0.54;
      const y1 = y0 - g.baseH - Math.sin(t * 0.6 + g.phase) * 3;

      ctx.lineWidth = g.width;
      ctx.beginPath();
      ctx.moveTo(x, y0);
      ctx.quadraticCurveTo(x + sway * 0.55, (y0 + y1) / 2, x + sway, y1);
      ctx.stroke();

      // Tiny highlight on some blades
      if ((g.phase * 10) % 1 < 0.2) {
        ctx.strokeStyle = 'rgba(163,176,158,0.25)';
        ctx.beginPath();
        ctx.moveTo(x + 0.5, y0 - 4);
        ctx.quadraticCurveTo(x + sway * 0.4, (y0 + y1) / 2, x + sway * 0.8, y1 - 1);
        ctx.stroke();
        ctx.strokeStyle = '#3f513b';
      }
    }

    // Ground
    ctx.fillStyle = '#141c17';
    ctx.fillRect(0, h * 0.53, w, h * 0.5);

    // Occasional small "life" (insects / seeds) — soft fascination
    ctx.fillStyle = 'rgba(232,217,184,0.65)';
    for (let i = 0; i < 5; i++) {
      const lx = ((i * 83 + t * 11) % (w + 40)) - 20;
      const ly = h * 0.51 + Math.sin(t * 1.6 + i) * 7;
      ctx.fillRect(lx, ly, 1.6, 1.6);
    }
  }
}
